// jshint esversion: 11
/**
 * Duration Slider with Radial Progress
 * Provides visual feedback for permit duration selection
 */

!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{},n=(new e.Error).stack;n&&(e._sentryDebugIds=e._sentryDebugIds||{},e._sentryDebugIds[n]="862f0e03-2cea-5f8e-aaf0-c83f67c80063")}catch(e){}}();
import * as luxon from 'https://cdn.jsdelivr.net/npm/luxon@3.6.1/+esm';

/**
 * Compute permit visit end date (matches registration half-day rules).
 * @returns {import('luxon').DateTime|null}
 */
export function computeVisitEndDate({
    startDateIso,
    durationRequested,
    durationPeriod = 'D',
    maxEndDateIso = null,
} = {}) {
    if (!startDateIso || !durationRequested) {
        return null;
    }
    const startDate = luxon.DateTime.fromISO(startDateIso).startOf('day');
    if (!startDate.isValid) {
        return null;
    }
    const today = luxon.DateTime.now().startOf('day');
    const isToday = startDate.hasSame(today, 'day');
    let endDate;
    if (durationPeriod === 'M') {
        endDate = isToday
            ? startDate.plus({months: durationRequested})
            : startDate.plus({months: durationRequested}).minus({days: 1});
    } else if (isToday) {
        endDate = startDate.plus({days: durationRequested});
    } else {
        endDate = startDate.plus({days: durationRequested - 1});
    }

    // Golden Ticket carve-out: a same-day override (max_end_date == today,
    // start == today) yields a partial day ending at midnight tonight rather
    // than the usual 24-hour minimum that would push end_date to tomorrow.
    // Cap the computed end_date to the override's max_end_date whenever the
    // natural arithmetic overshoots it.
    if (maxEndDateIso) {
        const cap = luxon.DateTime.fromISO(maxEndDateIso).startOf('day');
        if (cap.isValid && endDate > cap) {
            endDate = startDate > cap ? startDate : cap;
        }
    }

    return endDate;
}

/** @param {import('luxon').DateTime} dt */
export function formatVisitDateShort(dt) {
    return dt.toFormat('EEE, MMM d');
}

class DurationSlider {
    constructor() {
        this.slider = document.querySelector('#duration-requested');
        this.radialProgress = document.querySelector('.radial-progress');
        this.daysUsedEl = document.querySelector('.progress-text .days-used');
        this.daysAvailableEl = document.querySelector('.progress-text .days-available');
        this.startDateEl = document.querySelector('.duration-dates .start-date');
        this.endDateEl = document.querySelector('.duration-dates .end-date');
        this.durationSelect = this.slider; // The slider IS the duration input now
        this.visitBeginInput = document.querySelector('#visit-begin-date');
        
        // Use a private property with getter/setter to track changes
        this._maxDurationInDays = 14;
        this._inPeriodChange = false;
        this._maxEndDateIso = null;
        
        if (!this.slider || !this.durationSelect) {
            return;
        }
        
        this.init();
    }
    
    // Getter for maxDurationInDays
    get maxDurationInDays() {
        return this._maxDurationInDays;
    }
    
    // Setter for maxDurationInDays with protection
    set maxDurationInDays(value) {
        // During period changes, don't allow changes to maxDurationInDays
        if (this._inPeriodChange && value !== this._maxDurationInDays) {
            return;
        }
        
        // Don't allow setting to default if we have a real value
        if (value === 14 && this._maxDurationInDays > 14) {
            return;
        }
        
        this._maxDurationInDays = value;
    }
    
    init() {
        // Set up event listeners
        this.slider.addEventListener('input', () => this.handleSliderChange());
        this.durationSelect.addEventListener('change', () => this.handleSelectChange());
        
        if (this.visitBeginInput) {
            this.visitBeginInput.addEventListener('change', () => this.updateDates());
        }
        
        // Listen for duration period changes (Days/Months)
        const durationPeriodRadios = document.querySelectorAll('input[name="duration-period"]');
        durationPeriodRadios.forEach(radio => {
            radio.addEventListener('change', () => this.handlePeriodChange());
        });
        
        // Initialize display
        this.updateDisplay();
    }
    
    /**
     * Get the selected duration period (D for days, M for months)
     * @returns {string} 'D' or 'M'
     */
    getDurationPeriod() {
        const checkedRadio = document.querySelector('input[name="duration-period"]:checked');
        return checkedRadio ? checkedRadio.value : 'D';
    }
    
    /**
     * Handle duration period changes (Days/Months toggle).
     *
     * Visual contract: while the 500ms re-entry guard is active the
     * .period-toggle wrapper is marked data-busy="true" so the rapid
     * "Days/Months mash" rage-click pattern paints feedback even though
     * the underlying calculation is synchronous and instant. Without
     * this, the toggle silently absorbs the second tap and looks dead.
     */
    handlePeriodChange() {
        if (this._periodChangeInProgress) {
            return;
        }

        this._periodChangeInProgress = true;
        this._inPeriodChange = true;

        const toggleWrapper = this.slider.closest('.duration-range-container')
            ?.querySelector('.period-toggle');
        if (toggleWrapper) {
            toggleWrapper.dataset.busy = 'true';
            toggleWrapper.setAttribute('aria-busy', 'true');
        }

        try {
            this.updateSliderDisplay();
            this.syncFromSelect();
        } finally {
            setTimeout(() => {
                this._periodChangeInProgress = false;
                this._inPeriodChange = false;
                if (toggleWrapper && toggleWrapper.dataset.busy === 'true') {
                    delete toggleWrapper.dataset.busy;
                    toggleWrapper.removeAttribute('aria-busy');
                }
            }, 500);
        }
    }
    
    /**
     * Show or hide the Days/Months period toggle.
     * Only visible when the community supports permits longer than 31 days.
     * @param {boolean} visible
     */
    setPeriodToggleVisible(visible) {
        const toggle = document.querySelector('.period-toggle');
        if (!toggle) return;
        toggle.classList.toggle('visible', visible);
        if (!visible) {
            // Snap back to Days mode so Months selection doesn't persist invisibly.
            const daysRadio = document.querySelector('#duration-period-days');
            if (daysRadio && !daysRadio.checked) {
                daysRadio.checked = true;
                this.handlePeriodChange();
            }
        }
    }

    /**
     * Update the slider's maximum value
     * @param {number} maxDays - Maximum duration available (always in days)
     * @param {number} daysPerPermit - Community's days per permit limit (always in days)
     */
    /**
     * Cap the computed visit end date to a Golden Ticket override's
     * max_end_date so a same-day ticket ends at midnight tonight instead of
     * extending into tomorrow under the usual 24-hour minimum.
     * @param {string|null} maxEndDateIso ISO date (yyyy-MM-dd) or null to clear.
     */
    setMaxEndDate(maxEndDateIso) {
        this._maxEndDateIso = maxEndDateIso || null;
        this.updateDisplay();
    }

    updateMaxDuration(maxDays, daysPerPermit = null) {
        // Store the days value for later use
        // Apply community limit if it exists and is less than maxDays
        let maxDaysValue;
        
        // Handle explicit 0 (limits exceeded, no golden ticket)
        if (maxDays === 0) {
            maxDaysValue = 0;
        } else if (daysPerPermit && daysPerPermit > 0 && daysPerPermit < maxDays) {
            maxDaysValue = daysPerPermit;
        } else if (maxDays > 0) {
            maxDaysValue = maxDays;
        } else {
            maxDaysValue = 14; // Default fallback for undefined/null
        }
        
        // Guard: Don't overwrite with the default fallback (14) if we already have a real value
        // But DO allow setting to 0 (limits exceeded) or higher values (golden ticket)
        if (maxDaysValue === 14 && this.maxDurationInDays > 14) {
            // Still update the display with the current value
            this.updateSliderDisplay();
            this.syncFromSelect();
            return;
        }
        
        // Always store in days for future conversions
        this.maxDurationInDays = maxDaysValue;

        // Show Days/Months toggle only when community allows permits > 31 days.
        this.setPeriodToggleVisible(maxDaysValue > 31);
        
        // Update the slider display with new max
        this.updateSliderDisplay();
        
        // Sync with current select value after updating max
        this.syncFromSelect();
    }
    
    /**
     * Update the slider display based on current period mode
     * Does NOT change maxDurationInDays, only updates the slider max/display
     */
    updateSliderDisplay() {
        // Use the stored days value
        const maxDaysValue = this.maxDurationInDays;
        
        // Convert to appropriate units based on period mode
        const durationPeriod = this.getDurationPeriod();
        let sliderMax, displayMax;
        
        // Handle 0 explicitly (limits exceeded, no golden ticket)
        if (maxDaysValue === 0) {
            sliderMax = 0;
            displayMax = 0;
        } else if (durationPeriod === 'M') {
            // Months mode: convert days to months (divide by 30)
            // Use Math.floor to round down (e.g., 184 days = 6.13 months = 6 months)
            sliderMax = Math.floor(maxDaysValue / 30);
            
            // Ensure at least 1 month if we have any days available
            if (sliderMax < 1 && maxDaysValue > 0) {
                sliderMax = 1;
            }
            displayMax = sliderMax;
        } else {
            // Days mode: use as-is
            sliderMax = maxDaysValue;
            displayMax = maxDaysValue;
        }
        
        // For slider functionality, max must be at least 1 (can't drag to 0 max)
        // But display should still show the true value (0 when blocked)
        const sliderMaxAttr = sliderMax < 1 ? 1 : sliderMax;
        
        // Set both the property and the attribute to ensure DOM update
        this.slider.max = sliderMaxAttr;
        this.slider.setAttribute('max', String(sliderMaxAttr));
        
        // Update text with units - show actual available days (including 0)
        const unitText = durationPeriod === 'M' ? 'months' : 'days';
        this.daysAvailableEl.textContent = `${displayMax} ${unitText}`;
        
        // Ensure min is 0
        this.slider.min = 0;
        this.slider.setAttribute('min', '0');
        
        // Set default value to 1 if available and currently at 0
        // But don't do this if displayMax is 0 (blocked)
        if (displayMax > 0 && sliderMaxAttr >= 1 && parseInt(this.slider.value, 10) === 0) {
            this.slider.value = 1;
            // Trigger change to update display
            this.handleSliderChange();
        } else if (displayMax === 0) {
            // Force slider to 0 when blocked
            this.slider.value = 0;
        }
    }
    
    /**
     * Handle slider input changes
     */
    handleSliderChange() {
        const value = parseInt(this.slider.value, 10);
        
        // Update the select dropdown to match
        if (this.durationSelect) {
            // Only update if the value actually changed
            const currentSelectValue = parseInt(this.durationSelect.value, 10) || 0;
            if (currentSelectValue !== value) {
                this.durationSelect.value = value;
                // Trigger change event for other listeners
                this.durationSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
        
        this.updateDisplay();
    }
    
    /**
     * Handle select dropdown changes
     */
    handleSelectChange() {
        const value = parseInt(this.durationSelect.value, 10) || 0;
        
        // Update the slider to match
        if (this.slider) {
            this.slider.value = value;
            this.updateDisplay();
        }
    }
    
    /**
     * Sync slider from current select value (useful after Schedule rebuilds options)
     */
    syncFromSelect() {
        if (this.durationSelect && this.durationSelect.value) {
            const value = parseInt(this.durationSelect.value, 10) || 0;
            const maxValue = parseInt(this.slider.max, 10) || 0;
            
            // Clamp value to valid range
            const clampedValue = Math.max(0, Math.min(value, maxValue));
            this.slider.value = clampedValue;
            this.updateDisplay();
        }
    }
    
    /**
     * Update all visual elements
     */
    updateDisplay() {
        const durationRequested = parseInt(this.slider.value, 10) || 0;
        
        // Use the actual stored value for display (not slider.max which is forced to 1 min)
        // This ensures "0 days" is shown when limits are exceeded
        const durationPeriod = this.getDurationPeriod();
        let durationAvailable;
        if (durationPeriod === 'M') {
            durationAvailable = Math.floor(this.maxDurationInDays / 30);
            // At least 1 month if we have any days
            if (durationAvailable < 1 && this.maxDurationInDays > 0) {
                durationAvailable = 1;
            }
        } else {
            durationAvailable = this.maxDurationInDays;
        }
        
        // Update progress text
        this.daysUsedEl.textContent = durationRequested;
        
        // Format daysAvailable with units (days or months)
        const unitText = durationPeriod === 'M' ? 'months' : 'days';
        this.daysAvailableEl.textContent = `${durationAvailable} ${unitText}`;
        
        // Calculate percentage for radial progress (0-100)
        const percentage = durationAvailable > 0 ? (durationRequested / durationAvailable) * 100 : 0;
        this.radialProgress.style.setProperty('--progress', percentage.toFixed(1));
        
        // Update slider gradient. Set --progress as a unitless 0-100 number so
        // the CSS can inset the fill by half the thumb width (the fill stop must
        // land on the thumb *center*, which only travels thumbSize/2 .. width-thumbSize/2).
        const sliderPercentage = durationAvailable > 0 ? (durationRequested / durationAvailable) * 100 : 0;
        this.slider.style.setProperty('--progress', sliderPercentage.toFixed(1));
        
        // Update dates
        this.updateDates();
    }
    
    /**
     * Update start and end date displays
     */
    updateDates() {
        if (!this.visitBeginInput || !this.visitBeginInput.value) {
            this.startDateEl.textContent = '---';
            this.endDateEl.textContent = '---';
            document.dispatchEvent(new CustomEvent('permitVisitScheduleChanged'));
            return;
        }
        
        const durationRequested = parseInt(this.slider.value, 10) || 0;
        
        if (durationRequested === 0) {
            this.startDateEl.textContent = '---';
            this.endDateEl.textContent = '---';
            document.dispatchEvent(new CustomEvent('permitVisitScheduleChanged'));
            return;
        }

        const startDate = luxon.DateTime.fromISO(this.visitBeginInput.value).startOf('day');
        if (!startDate.isValid) {
            this.startDateEl.textContent = '---';
            this.endDateEl.textContent = '---';
            document.dispatchEvent(new CustomEvent('permitVisitScheduleChanged'));
            return;
        }

        const endDate = computeVisitEndDate({
            startDateIso: this.visitBeginInput.value,
            durationRequested,
            durationPeriod: this.getDurationPeriod(),
            maxEndDateIso: this._maxEndDateIso,
        });

        this.startDateEl.textContent = formatVisitDateShort(startDate);
        this.startDateEl.setAttribute('data-date', startDate.toISODate());

        if (endDate) {
            this.endDateEl.textContent = formatVisitDateShort(endDate);
            this.endDateEl.setAttribute('data-date', endDate.toISODate());
        } else {
            this.endDateEl.textContent = '---';
            this.endDateEl.removeAttribute('data-date');
        }

        document.dispatchEvent(new CustomEvent('permitVisitScheduleChanged'));
    }
    
    /**
     * Get current duration value
     * @returns {number}
     */
    getValue() {
        return parseInt(this.slider.value, 10) || 0;
    }
    
    /**
     * Set duration value
     * @param {number} value
     */
    setValue(value) {
        const sliderMax = parseInt(this.slider.max, 10);
        const clampedValue = Math.max(0, Math.min(value, sliderMax));
        this.slider.value = clampedValue;
        this.updateDisplay();
    }
}

export { DurationSlider };

//# debugId=862f0e03-2cea-5f8e-aaf0-c83f67c80063
